import React, { useState, useEffect, useCallback } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
	Box,
	Typography,
	Button,
	IconButton,
	Chip,
	LinearProgress,
} from "@mui/material";
import {
	Close as CloseIcon,
	PlayArrow as PlayArrowIcon,
	Pause as PauseIcon,
	SkipNext as SkipNextIcon,
	SkipPrevious as SkipPreviousIcon,
	FilterList as FilterListIcon,
	Search as SearchIcon,
	Edit as EditIcon,
	TableChart as TableChartIcon,
	Comment as CommentIcon,
	Speed as SpeedIcon,
	Security as SecurityIcon,
	Download as DownloadIcon,
	ToggleOn as ToggleOnIcon,
	Event as EventIcon,
	Info as InfoIcon,
} from "@mui/icons-material";

// Clicks a tour-controlled element (e.g. a modal close button) if it is present
// in the DOM. Used to auto-close modals the user opened during the tour.
const closeTourModal = (selector) => {
	try {
		const el = document.querySelector(selector);
		if (el) el.click();
	} catch (e) {
		// no-op — the element may not be rendered
	}
};

const VFFJoyrideTour = ({ isOpen, onClose }) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	// Tour steps for VFF page
	const steps = [
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<TableChartIcon sx={{ color: "#cc0000" }} />
						Vehicle Tracking Tour
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						Welcome to the Vehicle Tracking module - your comprehensive tool for
						managing pre-series validation and component testing in the MAN
						development process.
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
						<Chip
							icon={<FilterListIcon />}
							label="Advanced Filtering"
							size="small"
							sx={{ bgcolor: "#e3f2fd" }}
						/>
						<Chip
							icon={<SearchIcon />}
							label="Smart Search"
							size="small"
							sx={{ bgcolor: "#e8f5e8" }}
						/>
						<Chip
							icon={<CommentIcon />}
							label="Collaboration"
							size="small"
							sx={{ bgcolor: "#fff3e0" }}
						/>
						<Chip
							icon={<EventIcon />}
							label="Date Management"
							size="small"
							sx={{ bgcolor: "#fce4ec" }}
						/>
					</Box>
					<Typography
						variant="body2"
						sx={{ color: "#666", fontStyle: "italic" }}
					>
						This tour will guide you through all key features in about 3
						minutes.
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 10000,
				},
			},
		},
		{
			target: '[data-tour-target="vff-sidebar"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 2, color: "#1e2832" }}
					>
						Vehicle Tracking Filters Sidebar
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The Vehicle Tracking sidebar provides comprehensive filtering
						capabilities specific to vehicle tracking. Filter by projects,
						vehicle numbers, BZA codes, Requirement Dates, Part no Prefixes,
						Status Color, Responsible Person, and many more.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							VFF-Specific Filters:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• Project & Vehicle filtering • BZA code categorization • Emcos
							milestone tracking • Responsible person filtering • Date range
							selections
						</Typography>
					</Box>
				</Box>
			),
			placement: "right",
			spotlightPadding: 10,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="additional-filters-toggle"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<FilterListIcon sx={{ color: "#cc0000" }} />
						Additional Filters
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Beyond the main filters, there's a whole set of advanced filters.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the "Additional Filters" button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the panel opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "right",
			spotlightPadding: 6,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="additional-filters-modal"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<FilterListIcon sx={{ color: "#cc0000" }} />
						Additional Filters Panel
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This panel holds the advanced filtering options. Combine them with
						the main sidebar filters to precisely narrow down the vehicle
						tracking data you need.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Advanced Options:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• Extra status & category filters • Milestone-level selections •
							Fine-grained date ranges
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this panel for you.
					</Typography>
				</Box>
			),
			placement: "center",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-archive-button"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<InfoIcon sx={{ color: "#cc0000" }} />
						Archive Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the Archive button to change the archive status of a record.
						Click it to pick an option such as Unarchived, Archived, To be
						deleted, or Duplicate Entry.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Archive Options:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Unarchived / Archived • To be deleted • Duplicate Entry •
							Restricted to authorized users
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-search-bar"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SearchIcon sx={{ color: "#cc0000" }} />
						Search Bar
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Search across Vehicle Tracking data including vehicle numbers, part
						numbers. Real-time filtering.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e8f5e8",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #4caf50",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#2e7d32" }}
						>
							Search Capabilities:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#2e7d32" }}
						>
							• Vehicle Numbers • Instant results
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom-start",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="more-info-button"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<InfoIcon sx={{ color: "#cc0000" }} />
						More Info Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Each row has a "More info" button that opens a side panel with
						additional details about the part.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the "More info" button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the panel opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="vff-sidepanel"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<InfoIcon sx={{ color: "#cc0000" }} />
						More Info Panel
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This side panel shows extended details for the selected record —
						vehicle number, project, part number & description, supplier, ALG,
						change number and more, plus field-tracking worklogs.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Expanded Information:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Extended part details • Technical specifications • Additional
							metadata • Field-tracking worklogs
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this panel for you.
					</Typography>
				</Box>
			),
			placement: "left",
			spotlightPadding: 8,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="parts-comment-button"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<CommentIcon sx={{ color: "#cc0000" }} />
						Parts Comments
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Each row provides comment button for Parts column. Enable team
						collaboration with context-specific discussions for different
						aspects.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f1f8e9",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #689f38",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#558b2f" }}
						>
							Comment System:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#558b2f" }}
						>
							• Parts-specific comments • Team collaboration • Comment history
							tracking
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '[data-tour-target="status-toggle-parts"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<ToggleOnIcon sx={{ color: "#cc0000" }} />
						Status Toggle Controls
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Both Parts and Vehicle columns have status toggle switches with
						3-state functionality: None → In Progress → Completed. Click to
						cycle through states.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00" }}
						>
							3-State Toggle System:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							• None (Grey) - Not started • In Progress (Orange) - Work ongoing
							• Completed (Green) - Task finished • Click to cycle states
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target:
				'button[style*="background: #cc0000"], button[style*="#cc0000"], td button[title*="Edit"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<EditIcon sx={{ color: "#cc0000" }} />
						Edit Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the edit button to modify responsible person assignments for
						both Parts and Vehicle columns. Changes are saved immediately when
						you click the save button.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Edit Capabilities:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Parts responsible person • Vehicle responsible person • Inline
							editing mode • Immediate save/cancel
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target:
				'div[style*="cursor: pointer"][style*="display: flex"][style*="alignItems: center"]:has(svg), svg[width="16"]:first-of-type',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<EventIcon sx={{ color: "#cc0000" }} />
						Calendar Date Pickers
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The Vehicle Tracking module includes multiple date management
						features. The Dates column has calendar buttons for LTB dates, while
						Parts and Vehicle columns have followup date pickers, and Status
						column manages milestone dates.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f3e5f5",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #9c27b0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#7b1fa2" }}
						>
							Date Management:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#7b1fa2" }}
						>
							• LTB dates in Dates column • Parts followup dates • Vehicle
							followup dates • Status milestone dates • Interactive date pickers
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SpeedIcon sx={{ color: "#4caf50" }} />
						Vehicle Tracking Mastery!
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						You're now equipped to effectively use the Vehicle Tracking module
						for managing vehicle function validation and pre-series testing
						processes.
					</Typography>

					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 1.5, color: "#cc0000" }}
					>
						Key Vehicle Tracking Workflows:
					</Typography>
					<Box sx={{ mb: 2 }}>
						{[
							"Filter by vehicle/project first",
							"Use comment systems effectively",
							"Manage dates across multiple columns",
							"Monitor 3-state status progression",
							"Bulk Edit Records",
						].map((tip, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 0.5,
									color: "#555",
								}}
							>
								<Box
									sx={{
										width: 6,
										height: 6,
										bgcolor: "#cc0000",
										borderRadius: "50%",
										mr: 1,
									}}
								/>
								{tip}
							</Typography>
						))}
					</Box>

					<Typography
						variant="body2"
						sx={{
							color: "#666",
							fontStyle: "MAN Europe_Bold",
							textAlign: "center",
							mt: 2,
						}}
					>
						Happy Tracking! 🚚
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
		},
	];

	useEffect(() => {
		if (isOpen) {
			setRun(true);
			setStepIndex(0);
			setIsPlaying(true);
		} else {
			setRun(false);
		}
	}, [isOpen]);

	// On forced "click to open" steps, advance the tour only once the user
	// actually clicks the button (which opens the panel/side-panel).
	useEffect(() => {
		if (!run) return undefined;
		const currentTarget = steps[stepIndex]?.target;
		const forcedClickTargets = [
			'[data-tour-target="additional-filters-toggle"]',
			'[data-tour-target="more-info-button"]',
		];
		if (!forcedClickTargets.includes(currentTarget)) {
			return undefined;
		}
		const btn = document.querySelector(currentTarget);
		if (!btn) return undefined;
		const handler = () => {
			// Give the panel a moment to open before highlighting it.
			setTimeout(() => setStepIndex((i) => i + 1), 350);
		};
		btn.addEventListener("click", handler);
		return () => btn.removeEventListener("click", handler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex, run]);

	const handleJoyrideCallback = useCallback(
		(data) => {
			const { action, index, status, type, step } = data;

			if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
				// Auto-close panels the user opened on the step we're leaving.
				if (step?.target === '[data-tour-target="additional-filters-modal"]') {
					closeTourModal('[data-tour-target="additional-filters-close"]');
				}
				if (step?.target === '[data-tour-target="vff-sidepanel"]') {
					closeTourModal('[data-tour-target="vff-sidepanel-close"]');
				}
				const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
				setStepIndex(nextStepIndex);
			} else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
				closeTourModal('[data-tour-target="additional-filters-close"]');
				setRun(false);
				onClose();
			}
		},
		[onClose],
	);

	const handleClose = () => {
		// When user clicks the close (X) button, treat it as skipping the tour
		closeTourModal('[data-tour-target="additional-filters-close"]');
		setRun(false);
		onClose();
	};

	const handleSkipTour = () => {
		// When user clicks "Skip Tour" button, skip the entire tour
		closeTourModal('[data-tour-target="additional-filters-close"]');
		setRun(false);
		onClose();
	};

	const handlePlay = () => {
		setIsPlaying(!isPlaying);
		setRun(!isPlaying);
	};

	const progress = ((stepIndex + 1) / steps.length) * 100;
	// Custom tooltip component
	const CustomTooltip = ({
		continuous,
		index,
		step,
		backProps,
		closeProps,
		primaryProps,
		skipProps,
		tooltipProps,
	}) => (
		<Box
			{...tooltipProps}
			sx={{
				backgroundColor: "white",
				borderRadius: "12px",
				boxShadow: "0 8px 32px rgba(30, 40, 50, 0.15)",
				maxWidth: step.content.props.sx?.maxWidth || 400,
				overflow: "hidden",
				fontFamily: "MAN EUROPE_Regular",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					p: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: "bold",
						fontSize: "14px",
						fontFamily: "MAN EUROPE_Bold",
					}}
				>
					Vehicle Tracking Tour ({index + 1}/{steps.length})
				</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					<IconButton
						{...backProps}
						size="small"
						disabled={index === 0}
						sx={{ color: index === 0 ? "#666" : "white" }}
					>
						<SkipPreviousIcon fontSize="small" />
					</IconButton>
					<IconButton onClick={handlePlay} size="small" sx={{ color: "white" }}>
						{isPlaying ? (
							<PauseIcon fontSize="small" />
						) : (
							<PlayArrowIcon fontSize="small" />
						)}
					</IconButton>
					<IconButton
						onClick={handleSkipTour}
						size="small"
						sx={{ color: "white" }}
						title="Skip Tour"
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			{/* Progress Bar */}
			<Box sx={{ px: 2, pt: 1 }}>
				<LinearProgress
					variant="determinate"
					value={progress}
					sx={{
						height: 4,
						borderRadius: 2,
						backgroundColor: "#e0e0e0",
						"& .MuiLinearProgress-bar": {
							backgroundColor: "#cc0000",
						},
					}}
				/>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						textAlign: "center",
						mt: 0.5,
						color: "#666",
						fontSize: "12px",
					}}
				>
					{Math.round(progress)}% Complete
				</Typography>
			</Box>
			{/* Content */}
			<Box sx={{ p: 0 }}>{step.content}</Box> {/* Footer */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #eee",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					backgroundColor: "#f8f9fa",
				}}
			>
				<Button
					onClick={handleSkipTour}
					variant="text"
					sx={{
						color: "#666",
						fontSize: "12px",
						fontFamily: "MAN EUROPE_Regular",
					}}
				>
					Skip Tour
				</Button>
				<Box sx={{ display: "flex", gap: 1 }}>
					{index > 0 && (
						<Button
							{...backProps}
							variant="outlined"
							size="small"
							sx={{
								borderColor: "#cc0000",
								color: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#aa0000",
									backgroundColor: "#ffebee",
								},
							}}
						>
							Previous
						</Button>
					)}
					{step.hideNext ? (
						<Typography
							variant="caption"
							sx={{
								color: "#cc0000",
								fontFamily: "MAN EUROPE_Bold",
								fontSize: "12px",
								alignSelf: "center",
								paddingLeft:"10px"
							}}
						>
							Click highlighted button to continue
						</Typography>
					) : (
						<Button
							{...primaryProps}
							variant="contained"
							size="small"
							sx={{
								backgroundColor: "#cc0000",
								fontSize: "12px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									backgroundColor: "#aa0000",
								},
							}}
						>
							{index === steps.length - 1 ? "Finish" : "Next"}
						</Button>
					)}
				</Box>
			</Box>
		</Box>
	);
	return (
		<>
			{/* Custom CSS to fix spotlight visibility */}
			<style>{`
        .__floater__arrow polygon {
          fill: #fff !important;
        }
        
        /* Fix spotlight visibility - remove grey blur */
        [data-tour-elem="spotlight"] {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        
        .react-joyride__spotlight {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          mix-blend-mode: normal !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4),
            0 0 16px 3px rgba(255, 255, 255, 0.4) !important;
        }
        
        .react-joyride__overlay {
          mix-blend-mode: normal !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
        
        /* Ensure target elements are fully visible */
        .react-joyride__spotlight > * {
          filter: none !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Remove any filters that might cause blur */
        body.react-joyride__body--scrolling * {
          filter: none !important;
        }
        
        /* Ensure proper z-index for highlighted elements */
        .react-joyride__spotlight {
          z-index: 9998 !important;
        }
        
        .react-joyride__tooltip {
          z-index: 9999 !important;
        }
      `}</style>
			<Joyride
				callback={handleJoyrideCallback}
				continuous={true}
				run={run && isPlaying}
				scrollToFirstStep={true}
				showProgress={false}
				showSkipButton={true}
				stepIndex={stepIndex}
				steps={steps}
				tooltipComponent={CustomTooltip}
				disableScrolling={true}
				spotlightClicks={true}
				disableOverlayClose={true}
				styles={{
					options: {
						arrowColor: "#fff",
						backgroundColor: "#fff",
						overlayColor: "rgba(0, 0, 0, 0.4)",
						primaryColor: "#cc0000",
						textColor: "#333",
						width: undefined,
						zIndex: 10000,
					},
					overlay: {
						mixBlendMode: "normal",
						backgroundColor: "rgba(0, 0, 0, 0.4)",
					},
					spotlight: {
						borderRadius: "8px",
						border: "none",
						backgroundColor: "transparent",
						boxShadow:
							"0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 16px 3px rgba(255, 255, 255, 0.4)",
						filter: "none !important",
						opacity: 1,
						mixBlendMode: "normal",
					},
					spotlightLegacy: {
						borderRadius: "8px",
						backgroundColor: "transparent",
						filter: "none",
					},
				}}
				locale={{
					back: "Previous",
					close: "Close",
					last: "Finish",
					next: "Next",
					skip: "Skip Tour",
				}}
			/>
		</>
	);
};

export default VFFJoyrideTour;
